/**
 * R268cc · Feb 2026 — Import Preview Wizard.
 *
 * Two-step import UX for CSV / Excel / Google Sheets:
 *   1. Upload OR paste a sheet URL → server dry-runs the parse.
 *   2. Show sample rows + detected column mapping + errors.
 *   3. User confirms → commit.
 *
 * Talks to:
 *   POST /gst/workspaces/{ws}/import/invoices?dry_run=true|false
 *   POST /gst/workspaces/{ws}/import/invoices-from-sheet?dry_run=…
 */
import { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Upload, Link2, CheckCircle2, AlertTriangle, Table as TableIcon, Download } from "lucide-react";
import { fmtMoney } from "@/lib/currency";

export default function ImportWizard({ open, onClose, ws, onDone }) {
  const [mode, setMode] = useState("file");   // file | sheet
  const [sheetUrl, setSheetUrl] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);   // dry-run result
  const [loading, setLoading] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [templating, setTemplating] = useState(false);
  // R289f · Feb 2026 — Customer auto-link state.
  // Map raw customer name → { action: 'link'|'create'|'skip', client_id?, new_name?, gstin? }.
  const [linkMap, setLinkMap] = useState({});

  const reset = () => {
    setPreview(null); setFile(null); setSheetUrl(""); setMode("file");
    setLinkMap({});
  };

  const downloadTemplate = async () => {
    if (!ws) return;
    setTemplating(true);
    try {
      const r = await api.get(
        `/gst/workspaces/${ws}/import/invoices-template.xlsx`,
        { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "invoice-import-template.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiErrorMessage(e)); }
    finally { setTemplating(false); }
  };

  const doDryRun = async () => {
    if (!ws) return;
    setLoading(true); setPreview(null);
    try {
      let r;
      if (mode === "file") {
        if (!file) { toast.error("Choose a file first"); setLoading(false); return; }
        const fd = new FormData(); fd.append("file", file);
        r = await api.post(
          `/gst/workspaces/${ws}/import/invoices?dry_run=true`,
          fd, { headers: { "Content-Type": "multipart/form-data" } });
      } else {
        if (!sheetUrl) { toast.error("Paste a Google Sheets link"); setLoading(false); return; }
        r = await api.post(
          `/gst/workspaces/${ws}/import/invoices-from-sheet?dry_run=true`,
          { sheet_url: sheetUrl });
      }
      setPreview(r.data);
    } catch (e) { toast.error(apiErrorMessage(e)); }
    finally { setLoading(false); }
  };

  const doCommit = async () => {
    if (!ws || !preview) return;
    setCommitting(true);
    try {
      let r;
      // R289f · Only serialise entries the user actually made a
      // decision on. Auto-linked (exact/gstin match) rows can be
      // left out — the backend re-runs the match on commit.
      const cleanMap = {};
      Object.entries(linkMap).forEach(([k, v]) => {
        if (v && v.action && v.action !== "auto") cleanMap[k] = v;
      });
      if (mode === "file") {
        const fd = new FormData(); fd.append("file", file);
        if (Object.keys(cleanMap).length)
          fd.append("customer_link_map", JSON.stringify(cleanMap));
        r = await api.post(
          `/gst/workspaces/${ws}/import/invoices`,
          fd, { headers: { "Content-Type": "multipart/form-data" } });
      } else {
        r = await api.post(
          `/gst/workspaces/${ws}/import/invoices-from-sheet`,
          { sheet_url: sheetUrl,
             customer_link_map: Object.keys(cleanMap).length ? cleanMap : undefined });
      }
      toast.success(`Imported ${r.data.imported} · ${r.data.skipped} skipped`);
      onDone?.();
      reset();
      onClose?.();
    } catch (e) { toast.error(apiErrorMessage(e)); }
    finally { setCommitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { reset(); onClose?.(); } }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="import-wizard">
        <DialogHeader>
          <DialogTitle>Import invoices</DialogTitle>
          <DialogDescription>
            Upload a CSV / Excel or paste a Google Sheets link. Preview the parsed rows first — nothing is written until you hit Commit.
          </DialogDescription>
        </DialogHeader>

        {/* R289 · Feb 2026 — Template download + legacy explainer */}
        <div className="rounded-md border border-primary/30 bg-primary/5 p-3 text-xs space-y-2">
          <div className="flex items-center justify-between gap-2">
            <div>
              <div className="font-semibold text-primary">First time importing?</div>
              <div className="text-muted-foreground">
                Grab the Excel template with headers and two sample rows.
              </div>
            </div>
            <Button size="sm" variant="outline" onClick={downloadTemplate}
                       disabled={templating}
                       data-testid="import-template-download">
              <Download className="w-4 h-4 mr-1" />
              {templating ? "Preparing…" : "Download template"}
            </Button>
          </div>
          <div className="text-[11px] text-muted-foreground leading-relaxed">
            • Your original invoice number is <b>preserved verbatim</b> — no renumbering.<br />
            • Acquilix also stamps an internal <b>LEG-YYYY-YY-NNNN</b> reference so the historic set
              never touches the live <b>INV-YYYY-YY-NNNN</b> counter.<br />
            • Imported invoices flow into Sales list &amp; GST reports and remain editable.
          </div>
        </div>

        {/* Source picker */}
        <div className="flex gap-2 mb-2">
          <Button variant={mode === "file" ? "default" : "outline"} size="sm"
                    data-testid="import-mode-file"
                    onClick={() => { setMode("file"); setPreview(null); }}>
            <Upload className="w-4 h-4 mr-1" /> File
          </Button>
          <Button variant={mode === "sheet" ? "default" : "outline"} size="sm"
                    data-testid="import-mode-sheet"
                    onClick={() => { setMode("sheet"); setPreview(null); }}>
            <Link2 className="w-4 h-4 mr-1" /> Google Sheets link
          </Button>
        </div>

        {mode === "file" ? (
          <div>
            <Label className="text-xs">CSV / XLSX file (≤ 5MB)</Label>
            <Input type="file" accept=".csv,.xlsx,.xls"
                      data-testid="import-file-input"
                      onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); }} />
          </div>
        ) : (
          <div>
            <Label className="text-xs">Google Sheets URL (shared as Anyone with the link · Viewer)</Label>
            <Input value={sheetUrl || ""}
                      data-testid="import-sheet-url"
                      placeholder="https://docs.google.com/spreadsheets/d/…"
                      onChange={(e) => { setSheetUrl(e.target.value); setPreview(null); }} />
          </div>
        )}

        <div className="flex justify-end gap-2 py-2">
          <Button onClick={doDryRun} disabled={loading || committing}
                    data-testid="import-preview-btn">
            {loading ? "Parsing…" : <><TableIcon className="w-4 h-4 mr-1" /> Preview rows</>}
          </Button>
        </div>

        {preview && (
          <div className="space-y-3" data-testid="import-preview-panel">
            <div className="flex gap-4 text-sm">
              <span className="text-emerald-700 dark:text-emerald-300">
                Will import: <b>{preview.would_import ?? preview.imported}</b>
              </span>
              <span className="text-amber-700 dark:text-amber-300">
                Skipped (duplicates / errors): <b>{preview.skipped}</b>
              </span>
            </div>

            <div className="text-[11px] uppercase tracking-widest text-muted-foreground">
              Detected column mapping
            </div>
            <div className="flex flex-wrap gap-1">
              {Object.entries(preview.detected_headers || {}).map(([k, i]) => (
                <span key={k} className="text-[11px] border border-border rounded px-1.5 py-0.5">
                  <b>{k}</b> ← col {i}
                </span>
              ))}
            </div>

            {(preview.errors?.length || 0) > 0 && (
              <div className="border border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-950/40 rounded p-2 text-xs space-y-1">
                <div className="flex items-center gap-1 font-semibold text-amber-800 dark:text-amber-200">
                  <AlertTriangle className="w-3.5 h-3.5" /> {preview.errors.length} row{preview.errors.length === 1 ? "" : "s"} will be skipped
                </div>
                {preview.errors.slice(0, 5).map((e, i) => (
                  <div key={i}>Row {e.row}: {e.reason}</div>
                ))}
                {preview.errors.length > 5 && (
                  <div className="text-muted-foreground">…and {preview.errors.length - 5} more</div>
                )}
              </div>
            )}

            {(preview.customer_matches?.length || 0) > 0 && (
              <CustomerLinkPanel
                matches={preview.customer_matches}
                linkMap={linkMap}
                setLinkMap={setLinkMap}
              />
            )}

            {(preview.sample_docs?.length || 0) > 0 && (
              <div className="border border-border rounded overflow-x-auto">
                <table className="w-full text-xs" data-testid="import-preview-table">
                  <thead className="bg-muted/40 text-muted-foreground">
                    <tr>
                      <th className="text-left p-2">Doc #</th>
                      <th className="text-left p-2">Acquilix ref</th>
                      <th className="text-left p-2">Customer</th>
                      <th className="text-right p-2">Grand total</th>
                      <th className="text-left p-2">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.sample_docs.map((r, i) => (
                      <tr key={i} className="border-t border-border">
                        <td className="p-2 font-mono">{r.doc_no}</td>
                        <td className="p-2 font-mono text-muted-foreground">{r.internal_ref || "—"}</td>
                        <td className="p-2">{r.customer_name}</td>
                        <td className="p-2 text-right tabular-nums">{fmtMoney(r.grand_total, r.currency || "INR")}</td>
                        <td className="p-2">{r.status}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {preview.sample_docs.length < (preview.would_import ?? preview.imported) && (
                  <div className="text-[11px] text-muted-foreground p-2">
                    Showing first {preview.sample_docs.length} rows.
                    Actual commit will import all {(preview.would_import ?? preview.imported)}.
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => { reset(); onClose?.(); }}>Cancel</Button>
          <Button
            onClick={doCommit}
            disabled={!preview || committing || !(preview?.would_import > 0 || preview?.imported > 0)}
            data-testid="import-commit-btn"
          >
            {committing ? "Importing…" : (
              <><CheckCircle2 className="w-4 h-4 mr-1" />
                Commit import ({preview?.would_import ?? preview?.imported ?? 0})</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}



// R289f · Feb 2026 — Customer link picker. For every distinct raw
// customer name in the file we surface the top match + suggestions
// and let the user link to an existing client, create a new one, or
// skip (leaves the invoice unlinked).
function CustomerLinkPanel({ matches, linkMap, setLinkMap }) {
  const setRow = (key, patch) => {
    setLinkMap((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), ...patch } }));
  };
  const badgeFor = (conf) => ({
    exact: ["Auto-linked", "bg-emerald-100 text-emerald-800 border-emerald-300"],
    gstin: ["Auto-linked (GSTIN)", "bg-emerald-100 text-emerald-800 border-emerald-300"],
    partial: ["Likely match", "bg-amber-100 text-amber-800 border-amber-300"],
    none: ["No match", "bg-rose-100 text-rose-800 border-rose-300"],
  }[conf] || ["Unknown", "bg-muted"]);
  const needsAction = matches.filter((m) => m.confidence === "none" || m.confidence === "partial").length;

  return (
    <div className="border border-primary/30 bg-primary/5 rounded overflow-hidden" data-testid="import-customer-link-panel">
      <div className="px-3 py-2 border-b border-primary/20 flex items-center gap-2 flex-wrap">
        <strong className="text-xs uppercase tracking-widest">Customer link</strong>
        <span className="text-[11px] text-muted-foreground flex-1">
          {matches.length} unique customer{matches.length === 1 ? "" : "s"} in the file
          {needsAction > 0 ? ` · ${needsAction} need${needsAction === 1 ? "s" : ""} your attention` : " · everything matched"}
        </span>
      </div>
      <div className="max-h-72 overflow-y-auto divide-y divide-primary/10">
        {matches.map((m) => {
          const [label, cls] = badgeFor(m.confidence);
          const cur = linkMap[m.raw_name] || {};
          const action = cur.action || (m.confidence === "exact" || m.confidence === "gstin" ? "auto" : (m.matched_client ? "link" : "skip"));
          return (
            <div key={m.raw_name} className="px-3 py-2 text-xs grid grid-cols-[1fr_auto] gap-x-3 gap-y-1 items-center" data-testid={`import-cust-row-${m.raw_name}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium truncate">{m.raw_name || "(blank)"}</span>
                  {m.raw_gstin && <span className="font-mono text-[10px] text-muted-foreground">{m.raw_gstin}</span>}
                  <span className={`text-[10px] uppercase tracking-wider border rounded px-1 py-0.5 ${cls}`}>{label}</span>
                </div>
                {m.matched_client && (
                  <div className="text-[11px] text-muted-foreground mt-0.5 truncate">
                    Best match: <span className="text-foreground">{m.matched_client.name}</span>
                    {m.matched_client.gstin && <span className="ml-1 font-mono">({m.matched_client.gstin})</span>}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-1">
                <select
                  className="text-xs border border-border bg-background rounded px-1 py-1"
                  value={action}
                  onChange={(e) => setRow(m.raw_name, { action: e.target.value })}
                  data-testid={`import-cust-action-${m.raw_name}`}
                >
                  {(m.confidence === "exact" || m.confidence === "gstin") && <option value="auto">Auto-linked</option>}
                  <option value="link">Link to existing…</option>
                  <option value="create">Create new client</option>
                  <option value="skip">Skip — leave unlinked</option>
                </select>
                {action === "link" && (
                  <select
                    className="text-xs border border-border bg-background rounded px-1 py-1 max-w-[200px]"
                    value={cur.client_id || m.matched_client?.id || ""}
                    onChange={(e) => setRow(m.raw_name, { client_id: e.target.value })}
                    data-testid={`import-cust-link-${m.raw_name}`}
                  >
                    {[
                      ...(m.matched_client ? [m.matched_client] : []),
                      ...(m.suggestions || []).filter((s) => s.id !== m.matched_client?.id),
                    ].map((s) => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                    {!m.matched_client && (m.suggestions?.length || 0) === 0 && (
                      <option value="">No suggestions</option>
                    )}
                  </select>
                )}
                {action === "create" && (
                  <input
                    type="text"
                    className="text-xs border border-border bg-background rounded px-2 py-1 max-w-[200px]"
                    placeholder="New client name"
                    value={cur.new_name || m.raw_name}
                    onChange={(e) => setRow(m.raw_name, { new_name: e.target.value })}
                    data-testid={`import-cust-newname-${m.raw_name}`}
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
